import { Box, IconButton, Typography } from "@mui/material";
import { Waveform } from "@pages:app/audio/components/Waveform";
import { AudioPlayer, Waveform as WaveformData } from "@pages:app/audio/hooks";
import { formatClock } from "@pages:app/audio/util";
import { useTranslation } from "react-i18next";
import { MdPause, MdPlayArrow } from "react-icons/md";

interface RecordingPlayerProps {
  player: AudioPlayer;
  waveform: WaveformData | null;
  barColor?: (index: number) => string | null;
  reveal?: boolean;
  sweep?: boolean;
}

/*
 * Play button, the waveform as seek bar, and the clock.
 * Without a decodable waveform, show a plain track.
 */
export const RecordingPlayer = ({
  player,
  waveform,
  barColor,
  reveal = false,
  sweep = false,
}: RecordingPlayerProps) => {
  const { t } = useTranslation();
  const duration = player.duration || waveform?.durationSeconds || 0;
  const progress =
    player.ready && duration > 0
      ? Math.min(1, player.currentTime / duration)
      : null;
  const seek = player.ready
    ? (fraction: number) => player.seek(fraction * duration)
    : undefined;
  const peaks = waveform?.peaks ?? [];

  if (!player.ready && peaks.length === 0) {
    // TODO: Have the backend return the audio instead of relying on the browser
    return player.unplayable ? (
      <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
        {t("audio.player.unplayableLong")}
      </Typography>
    ) : null;
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      {player.ready && (
        <IconButton
          aria-label={
            player.isPlaying ? t("audio.player.pause") : t("audio.player.play")
          }
          onClick={player.toggle}
          color="primary"
          sx={{ flex: "0 0 auto" }}
        >
          {player.isPlaying ? <MdPause size={26} /> : <MdPlayArrow size={26} />}
        </IconButton>
      )}

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {peaks.length > 0 ? (
          <Waveform
            peaks={peaks}
            barColor={barColor}
            progress={progress}
            onSeek={seek}
            reveal={reveal}
            sweep={sweep}
          />
        ) : (
          <Waveform
            peaks={new Array(160).fill(0.12)}
            progress={progress}
            onSeek={seek}
            height={24}
            sweep={sweep}
          />
        )}
      </Box>

      {player.unplayable && (
        <Typography
          sx={{ flex: "0 0 auto", fontSize: 12, color: "text.secondary" }}
        >
          {t("audio.player.unplayableShort")}
        </Typography>
      )}

      {player.ready && (
        <Typography
          sx={{
            flex: "0 0 auto",
            fontSize: 12,
            fontWeight: 500,
            color: "text.secondary",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {formatClock(player.currentTime)} / {formatClock(duration)}
        </Typography>
      )}
    </Box>
  );
};
